import { useState } from 'react';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { Dialog } from '@/ui/Dialog';
import { Field, Input, Textarea } from '@/ui/Field';
import { SkeletonFilas } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import {
  useAceptaciones, useDocumentoLegal, useDocumentosLegales, useGuardarEstadoLegal,
  usePublicarVersionLegal, type DocumentoLegal,
} from './api';

/**
 * Legal y Datos — términos, autorizaciones y aviso de privacidad (Ley 1581/2012).
 *
 * ## Lo que cambia
 *
 * - **Las aceptaciones se fechan en la hora de quien mira.** Era
 *   `a.accepted_at?.slice(0,19).replace('T',' ')`: UTC en crudo, cinco horas por
 *   delante en Bogotá. En una tabla de consentimientos la hora exacta ES el
 *   dato: es la prueba de cuándo alguien autorizó el tratamiento.
 * - **Ver las aceptaciones ya no depende del título.** Se pasaba por la URL del
 *   `onclick` quitándole los apóstrofos (`'${d.title.replace(/'/g,"")}'`), así
 *   que el diálogo mostraba un título distinto del real.
 * - **Publicar una versión avisa de lo que provoca antes, no después.** Obliga a
 *   re-aceptar a TODOS en su próximo ingreso, y eso se lee en el propio botón.
 */
export function Legal() {
  const consulta = useDocumentosLegales(true);
  const [editando, setEditando] = useState<DocumentoLegal | null>(null);
  const [viendo, setViendo] = useState<DocumentoLegal | null>(null);

  const docs = consulta.data ?? [];

  return (
    <>
      <header className="mb-4">
        <h2 className="text-4 font-bold text-brand-ink">Documentos legales y consentimiento</h2>
        <p className="mt-1 text-3 text-muted">
          Términos, autorizaciones y aviso de privacidad. Al publicar una versión nueva, todos
          los usuarios tendrán que volver a aceptar en su próximo ingreso.
        </p>
      </header>

      {consulta.isPending ? (
        <SkeletonFilas filas={4} columnas={6} />
      ) : consulta.isError ? (
        <EstadoError
          err={consulta.error}
          que="la configuración legal"
          onReintentar={() => void consulta.refetch()}
        />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-3">
            <caption className="sr-only">Documentos legales del portal</caption>
            <thead className="bg-sunken text-2">
              <tr>
                <th scope="col" className="p-2 text-left font-semibold">Documento</th>
                <th scope="col" className="p-2 text-left font-semibold">Tipo</th>
                <th scope="col" className="p-2 text-left font-semibold">Versión</th>
                <th scope="col" className="p-2 text-left font-semibold">Estado</th>
                <th scope="col" className="p-2 text-right font-semibold">Aceptaciones</th>
                <th scope="col" className="p-2 text-right font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.id} className="border-t border-line">
                  <td className="p-2">
                    <strong className="text-brand-ink">{d.title}</strong>
                    <span className="block text-2 text-muted">{d.doc_key}</span>
                  </td>
                  <td className="p-2">
                    <Badge tone={d.is_mandatory ? 'danger' : d.is_sensitive ? 'warn' : 'neutral'}>
                      {d.is_mandatory ? 'Obligatorio' : d.is_sensitive ? 'Sensible' : 'Informativo'}
                    </Badge>
                  </td>
                  <td className="p-2">
                    {d.current_version
                      ? `v${d.current_version}`
                      : <span className="text-muted">sin publicar</span>}
                  </td>
                  <td className="p-2">
                    <Badge tone={d.is_active ? 'ok' : 'neutral'}>
                      {d.is_active ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </td>
                  <td className="p-2 text-right tabular-nums">{d.acceptances}</td>
                  <td className="whitespace-nowrap p-1 text-right">
                    <Button variant="ghost" size="sm" onClick={() => setEditando(d)}>
                      <i className="bi bi-pencil" aria-hidden="true" />
                      <span className="sr-only">Editar {d.title}</span>
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setViendo(d)}>
                      <i className="bi bi-list-check" aria-hidden="true" />
                      <span className="sr-only">Ver las aceptaciones de {d.title}</span>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {editando && <EditorLegal doc={editando} onCerrar={() => setEditando(null)} />}
      {viendo && <Aceptaciones doc={viendo} onCerrar={() => setViendo(null)} />}
    </>
  );
}

/** La siguiente versión menor: 1.3 → 1.4, nada → 1.0. */
function siguienteVersion(v?: string | null): string {
  if (!v) return '1.0';
  const partes = String(v).split('.');
  return `${partes[0]}.${parseInt(partes[1] || '0', 10) + 1}`;
}

function EditorLegal({ doc, onCerrar }: { doc: DocumentoLegal; onCerrar: () => void }) {
  const ficha = useDocumentoLegal(doc.id);
  const guardarEstado = useGuardarEstadoLegal();
  const publicar = usePublicarVersionLegal();

  const d = ficha.data;
  const vigente = d?.versions?.find((v) => v.is_current) ?? d?.versions?.at(-1);

  const [listo, setListo] = useState(false);
  const [activo, setActivo] = useState(doc.is_active);
  const [obligatorio, setObligatorio] = useState(doc.is_mandatory);
  const [contenido, setContenido] = useState('');
  const [version, setVersion] = useState('');
  const [resumen, setResumen] = useState('');

  if (!listo && d) {
    setActivo(d.is_active);
    setObligatorio(d.is_mandatory);
    setContenido(vigente?.content ?? '');
    setVersion(siguienteVersion(vigente?.version));
    setListo(true);
  }

  return (
    <Dialog
      abierto
      onCambio={(v) => { if (!v) onCerrar(); }}
      ancho="max-w-3xl"
      titulo={doc.title}
      descripcion="Publicar contenido nuevo crea una versión y exige re-aceptación."
      pie={
        <>
          <Button variant="ghost" onClick={onCerrar}>Cancelar</Button>
          <Button
            variant="secondary"
            loading={guardarEstado.isPending}
            onClick={() => guardarEstado.mutate(
              { id: doc.id, is_active: activo, is_mandatory: obligatorio },
              {
                onSuccess: () => { Utils.toast('Estado actualizado', 'success'); onCerrar(); },
                onError: (e) => Utils.toast((e as Error).message, 'error'),
              })}
          >
            Solo guardar el estado
          </Button>
          <Button
            loading={publicar.isPending}
            disabled={!version.trim() || !contenido.trim()}
            razonDeshabilitado="La versión y el contenido son obligatorios."
            onClick={() => {
              // El estado va primero, como en el vanilla: si se publica una
              // versión de un documento que además se acaba de activar, las dos
              // cosas tienen que quedar.
              guardarEstado.mutate(
                { id: doc.id, is_active: activo, is_mandatory: obligatorio },
                {
                  onSuccess: () => publicar.mutate(
                    {
                      id: doc.id,
                      version: version.trim(),
                      content: contenido.trim(),
                      summary: resumen.trim(),
                    },
                    {
                      onSuccess: () => {
                        Utils.toast(
                          `Versión ${version.trim()} publicada. Se exigirá re-aceptación.`,
                          'success');
                        onCerrar();
                      },
                      onError: (e) => Utils.toast((e as Error).message, 'error'),
                    }),
                  onError: (e) => Utils.toast((e as Error).message, 'error'),
                });
            }}
          >
            Publicar versión
          </Button>
        </>
      }
    >
      {ficha.isPending ? (
        <SkeletonFilas filas={5} columnas={1} />
      ) : ficha.isError ? (
        <EstadoError
          err={ficha.error}
          que="el documento legal"
          onReintentar={() => void ficha.refetch()}
        />
      ) : (
        <div className="flex flex-col gap-3">
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="flex items-start gap-2 text-3">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={activo}
                onChange={(e) => setActivo(e.target.checked)}
              />
              <span>Documento activo (visible para los usuarios)</span>
            </label>
            <label className="flex items-start gap-2 text-3">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={obligatorio}
                onChange={(e) => setObligatorio(e.target.checked)}
              />
              <span>Obligatorio (bloquea el acceso hasta aceptar)</span>
            </label>
          </div>

          <Field
            label="Contenido de la nueva versión"
            ayuda="Se admite HTML básico: h3, p, ul, li, strong."
          >
            <Textarea
              rows={11}
              className="font-mono text-2"
              value={contenido}
              onChange={(e) => setContenido(e.target.value)}
            />
          </Field>

          <div className="grid gap-3 sm:grid-cols-[8rem_1fr]">
            <Field label="Nueva versión">
              <Input value={version} onChange={(e) => setVersion(e.target.value)} />
            </Field>
            <Field label="Resumen de cambios" opcional>
              <Input
                value={resumen}
                onChange={(e) => setResumen(e.target.value)}
                placeholder="Ej. actualización de finalidades"
              />
            </Field>
          </div>

          <p className="text-2 text-muted">
            Versión vigente:{' '}
            <strong>{vigente?.version ? `v${vigente.version}` : 'ninguna'}</strong>. Publicar
            crea una nueva y obliga a todos a volver a aceptar.
          </p>
        </div>
      )}
    </Dialog>
  );
}

function Aceptaciones({ doc, onCerrar }: { doc: DocumentoLegal; onCerrar: () => void }) {
  const consulta = useAceptaciones(doc.id);
  const d = consulta.data;

  return (
    <Dialog
      abierto
      onCambio={(v) => { if (!v) onCerrar(); }}
      ancho="max-w-3xl"
      titulo={`Aceptaciones · ${doc.title}`}
      pie={
        <>
          <Button variant="ghost" onClick={onCerrar}>Cerrar</Button>
          <Button
            onClick={() => Utils.downloadAuthed(
              `/api/legal/admin/documents/${doc.id}/acceptances/export`, 'aceptaciones.xlsx')}
          >
            <i className="bi bi-download" aria-hidden="true" /> Exportar a Excel
          </Button>
        </>
      }
    >
      {consulta.isPending ? (
        <SkeletonFilas filas={6} columnas={5} />
      ) : consulta.isError ? (
        <EstadoError
          err={consulta.error}
          que="las aceptaciones"
          onReintentar={() => void consulta.refetch()}
        />
      ) : (
        <>
          <p className="mb-2 text-3 text-muted">
            Total: <strong>{d?.total ?? 0}</strong>{' '}
            {(d?.total ?? 0) === 1 ? 'registro' : 'registros'}.
          </p>
          <div className="max-h-[55vh] overflow-auto rounded-sm border border-line">
            <table className="w-full text-2">
              <caption className="sr-only">Quién aceptó {doc.title}, y cuándo</caption>
              <thead className="sticky top-0 bg-sunken">
                <tr>
                  <th scope="col" className="p-2 text-left font-semibold">Usuario</th>
                  <th scope="col" className="p-2 text-left font-semibold">Versión</th>
                  <th scope="col" className="p-2 text-left font-semibold">Estado</th>
                  <th scope="col" className="p-2 text-left font-semibold">Fecha</th>
                  <th scope="col" className="p-2 text-left font-semibold">IP</th>
                </tr>
              </thead>
              <tbody>
                {!d?.items.length ? (
                  <tr>
                    <td colSpan={5} className="p-6 text-center text-muted">Sin registros aún.</td>
                  </tr>
                ) : d.items.map((a, i) => (
                  <tr key={`${a.email}-${a.version}-${i}`} className="border-t border-line">
                    <td className="p-2">
                      {a.full_name}
                      <span className="block text-1 text-muted">{a.email}</span>
                    </td>
                    <td className="p-2">v{a.version}</td>
                    <td className="p-2">
                      <Badge tone={a.accepted ? 'ok' : 'danger'}>
                        {a.accepted ? 'Aceptado' : 'Sin aceptar'}
                      </Badge>
                    </td>
                    <td className="whitespace-nowrap p-2">
                      {/* La hora de quien mira. Aquí la hora ES el dato: es la
                          prueba de cuándo se autorizó el tratamiento. */}
                      {a.accepted_at ? Utils.fmtDatetime(a.accepted_at) : '—'}
                    </td>
                    <td className="p-2 font-mono text-muted">{a.ip_address || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Dialog>
  );
}
